/**
 * Language detection for redaction routing + UI.
 *
 * Wraps tinyld (the "light" build, ~20 KB gzipped) and lazy-loads it
 * the first time we need it. The detector classifies text into ISO
 * 639-1/3 codes; we map a useful subset to display names and a
 * support-level annotation that reflects how well the underlying
 * privacy filter handles each script.
 *
 * Per the OpenAI Privacy Filter model card, Latin / Cyrillic / Arabic
 * / CJK scripts are well-supported; Indic scripts (Devanagari, Bengali,
 * Tamil, Telugu, Gujarati, Punjabi) are weaker. Everything else
 * defaults to 'full' since the model is multilingual at the embedding
 * level.
 */

export type SupportLevel = 'full' | 'partial';

export type DetectedLanguage = {
  /** ISO 639-1 (or 639-3 for languages without a 2-letter code). */
  code: string;
  /** Human-readable name for the sidebar header. */
  name: string;
  supportLevel: SupportLevel;
  /** Confidence in [0..1]; lower than ~0.4 means we can't trust it. */
  confidence: number;
};

type TinyldModule = {
  detect: (text: string, opts?: { only?: string[] }) => string;
  detectAll: (text: string, opts?: { only?: string[] }) => { lang: string; accuracy: number }[];
};

let modPromise: Promise<TinyldModule> | null = null;
const loadTinyld = async (): Promise<TinyldModule> => {
  modPromise ??= import('tinyld/light').then((m) => m as unknown as TinyldModule);
  return modPromise;
};

/** Below this length, language detection is unreliable; skip it. */
const MIN_LENGTH = 20;
/** Below this confidence, surface as undefined rather than mislead. */
const MIN_CONFIDENCE = 0.4;

const NAMES: Record<string, string> = {
  af: 'Afrikaans',
  ar: 'Arabic',
  bg: 'Bulgarian',
  bn: 'Bengali',
  cs: 'Czech',
  da: 'Danish',
  de: 'German',
  el: 'Greek',
  en: 'English',
  es: 'Spanish',
  et: 'Estonian',
  fa: 'Persian',
  fi: 'Finnish',
  fr: 'French',
  gu: 'Gujarati',
  he: 'Hebrew',
  hi: 'Hindi',
  hr: 'Croatian',
  hu: 'Hungarian',
  id: 'Indonesian',
  it: 'Italian',
  ja: 'Japanese',
  ko: 'Korean',
  lt: 'Lithuanian',
  lv: 'Latvian',
  mr: 'Marathi',
  nl: 'Dutch',
  no: 'Norwegian',
  pa: 'Punjabi',
  pl: 'Polish',
  pt: 'Portuguese',
  ro: 'Romanian',
  ru: 'Russian',
  sk: 'Slovak',
  sl: 'Slovenian',
  sr: 'Serbian',
  sv: 'Swedish',
  ta: 'Tamil',
  te: 'Telugu',
  th: 'Thai',
  tr: 'Turkish',
  uk: 'Ukrainian',
  ur: 'Urdu',
  vi: 'Vietnamese',
  zh: 'Chinese',
  cn: 'Chinese',
};

/**
 * Languages whose script the underlying privacy-filter model handles
 * less reliably. We still run detection — the user just gets a warning.
 */
const PARTIAL_SUPPORT: ReadonlySet<string> = new Set([
  'hi', // Hindi (Devanagari)
  'mr', // Marathi (Devanagari)
  'bn', // Bengali
  'gu', // Gujarati
  'pa', // Punjabi (Gurmukhi)
  'ta', // Tamil
  'te', // Telugu
]);

const supportLevelFor = (code: string): SupportLevel =>
  PARTIAL_SUPPORT.has(code) ? 'partial' : 'full';

const nameFor = (code: string): string => NAMES[code] ?? code.toUpperCase();

/**
 * Detect the dominant language of `text`. Returns undefined for empty,
 * very short, or low-confidence inputs — caller should treat that as
 * "unknown" rather than render an incorrect badge.
 */
export const detectLanguage = async (
  text: string,
): Promise<DetectedLanguage | undefined> => {
  if (text.length < MIN_LENGTH) return undefined;
  let mod: TinyldModule;
  try {
    mod = await loadTinyld();
  } catch {
    return undefined;
  }
  const ranked = mod.detectAll(text);
  const top = ranked[0];
  if (!top) return undefined;
  if (top.accuracy < MIN_CONFIDENCE) return undefined;
  return {
    code: top.lang,
    name: nameFor(top.lang),
    supportLevel: supportLevelFor(top.lang),
    confidence: top.accuracy,
  };
};

/** Test-only sync helper exposing the name + support tables. */
export const _internals = { nameFor, supportLevelFor, NAMES, PARTIAL_SUPPORT };
